import React from 'react';
import styled from 'styled-components';
import { Bell, AlertTriangle, Snowflake, CreditCard, Calendar, FileText, CheckCircle } from 'lucide-react';

interface AlertsSummary {
  coldLeadsCount: number;
  overduePaymentsCount: number;
  upcomingCommitteesCount: number;
  pendingDocumentsCount: number;
}

interface Props {
  data: AlertsSummary;
}

const Container = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  padding: 28px;
  margin-bottom: 28px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const HeaderIcon = styled.div<{ $hasAlerts?: boolean }>`
  display: flex;
  align-items: center;
  color: ${props => props.$hasAlerts ? 'var(--error-color)' : 'var(--primary-color)'};
  filter: saturate(0.7) brightness(0.9);
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AlertBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: var(--error-light);
  color: var(--error-color);
  filter: saturate(0.7) brightness(0.85);
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 20px 0;
  padding-right: 52px;
`;

const AlertsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
`;

const AlertCard = styled.div<{ $severity: 'high' | 'medium' | 'info' }>`
  background: transparent;
  border-radius: 14px;
  padding: 20px;
  padding-top: 24px;
  position: relative;
  overflow: hidden;
  transition: all 0.2s ease;
  border-right: 3px solid ${props => {
    if (props.$severity === 'high') return 'var(--error-color)';
    if (props.$severity === 'medium') return 'var(--warning-color)';
    return 'var(--primary-color)';
  }};

  &:hover {
    background: #fafbfc;
  }
`;

const AlertHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const AlertIcon = styled.div<{ $severity: 'high' | 'medium' | 'info' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => {
    if (props.$severity === 'high') return 'var(--error-color)';
    if (props.$severity === 'medium') return 'var(--warning-color)';
    return 'var(--primary-color)';
  }};
  filter: saturate(0.7) brightness(0.9);
`;

const AlertCount = styled.div<{ $severity: 'high' | 'medium' | 'info' }>`
  font-size: 32px;
  font-weight: 700;
  color: ${props => {
    if (props.$severity === 'high') return 'var(--error-color)';
    if (props.$severity === 'medium') return 'var(--warning-color)';
    return 'var(--primary-color)';
  }};
  filter: saturate(0.7) brightness(0.85);
`;

const AlertTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
`;

const AlertDescription = styled.div`
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const NoAlertsCard = styled.div`
  grid-column: 1 / -1;
  background: #fafbfc;
  border-radius: 14px;
  padding: 40px;
  padding-top: 44px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--success-color);
    filter: saturate(0.6) brightness(0.95);
  }
`;

const NoAlertsIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: var(--success-color);
  filter: saturate(0.7) brightness(0.9);
`;

const NoAlertsText = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: var(--success-color);
  margin-bottom: 4px;
  filter: saturate(0.7) brightness(0.85);
`;

const NoAlertsSubtext = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
`;

const AdminAlertsWidget: React.FC<Props> = ({ data }) => {
  // Provide default values if data is undefined or incomplete
  const safeData: AlertsSummary = data || {
    coldLeadsCount: 0,
    overduePaymentsCount: 0,
    upcomingCommitteesCount: 0,
    pendingDocumentsCount: 0,
  };

  const totalAlerts =
    safeData.coldLeadsCount +
    safeData.overduePaymentsCount +
    safeData.upcomingCommitteesCount +
    safeData.pendingDocumentsCount;

  const hasHighPriorityAlerts = safeData.coldLeadsCount > 0 || safeData.overduePaymentsCount > 0;

  return (
    <Container>
      <Header>
        <HeaderIcon $hasAlerts={hasHighPriorityAlerts}>
          <Bell size={20} />
        </HeaderIcon>
        <Title>
          התראות ומשימות דחופות
          {hasHighPriorityAlerts && (
            <AlertBadge>
              <AlertTriangle size={12} />
              דורש תשומת לב
            </AlertBadge>
          )}
        </Title>
      </Header>
      <Subtitle>
        {totalAlerts > 0
          ? `יש ${totalAlerts} פריטים הדורשים תשומת לב`
          : 'אין התראות דחופות כרגע'}
      </Subtitle>

      {totalAlerts === 0 ? (
        <NoAlertsCard>
          <NoAlertsIconWrapper>
            <CheckCircle size={32} />
          </NoAlertsIconWrapper>
          <NoAlertsText>מצוין! הכל בסדר</NoAlertsText>
          <NoAlertsSubtext>אין משימות דחופות או התראות כרגע</NoAlertsSubtext>
        </NoAlertsCard>
      ) : (
        <AlertsGrid>
          {safeData.coldLeadsCount > 0 && (
            <AlertCard $severity="high">
              <AlertHeader>
                <AlertIcon $severity="high">
                  <Snowflake size={28} />
                </AlertIcon>
                <AlertCount $severity="high">{safeData.coldLeadsCount}</AlertCount>
              </AlertHeader>
              <AlertTitle>לידים קרים</AlertTitle>
              <AlertDescription>
                לידים ללא מעקב מעל 3 ימים - דורשים טיפול מיידי
              </AlertDescription>
            </AlertCard>
          )}

          {safeData.overduePaymentsCount > 0 && (
            <AlertCard $severity="high">
              <AlertHeader>
                <AlertIcon $severity="high">
                  <CreditCard size={28} />
                </AlertIcon>
                <AlertCount $severity="high">{safeData.overduePaymentsCount}</AlertCount>
              </AlertHeader>
              <AlertTitle>פיגורי תשלום</AlertTitle>
              <AlertDescription>
                לקוחות בפיגור תשלום - נדרש מעקב וגביה
              </AlertDescription>
            </AlertCard>
          )}

          {safeData.upcomingCommitteesCount > 0 && (
            <AlertCard $severity="medium">
              <AlertHeader>
                <AlertIcon $severity="medium">
                  <Calendar size={28} />
                </AlertIcon>
                <AlertCount $severity="medium">{safeData.upcomingCommitteesCount}</AlertCount>
              </AlertHeader>
              <AlertTitle>ועדות קרובות</AlertTitle>
              <AlertDescription>
                ועדות השבוע הקרוב - יש לוודא הכנה מלאה
              </AlertDescription>
            </AlertCard>
          )}

          {safeData.pendingDocumentsCount > 0 && (
            <AlertCard $severity="info">
              <AlertHeader>
                <AlertIcon $severity="info">
                  <FileText size={28} />
                </AlertIcon>
                <AlertCount $severity="info">{safeData.pendingDocumentsCount}</AlertCount>
              </AlertHeader>
              <AlertTitle>מסמכים ממתינים</AlertTitle>
              <AlertDescription>
                מסמכים ממתינים לאישור או טיפול
              </AlertDescription>
            </AlertCard>
          )}
        </AlertsGrid>
      )}
    </Container>
  );
};

export default AdminAlertsWidget;
